<!--
Copyright (C) 2020 GanttProject Cloud OU
Author: Dmitry Kazakov, Dmitry Barashev

This component shows a resource capacity heatmap graph and a few controls for the graph content filtering.
-->
<template>
    <div class="h-100">
        <div class="h-100" id="heatmap-tab">
            <div class="container-fluid h-100 d-flex flex-column">
                <div class="row">
                    <div class="col-12 col-md-6">
                        <v-select class="filter-dropdown mb-2"
                                  multiple
                                  v-bind:closeOnSelect="false"
                                  placeholder="Select projects..."
                                  label="name"
                                  @input="updateGraphWithDelay"
                                  v-model="selectedProjects"
                                  :options="projects"></v-select>
                    </div>
                    <div class="col-12 col-md-6">
                        <v-select class="filter-dropdown mb-2"
                                  multiple
                                  v-bind:closeOnSelect="false"
                                  placeholder="Select resources..."
                                  label="name"
                                  @input="updateGraphWithDelay"
                                  v-model="selectedResources"
                                  :options="resources"></v-select>
                    </div>
                </div>
                <div id="heatmap" class="mt-2">
                    <MxGraphChart ref="heatmap" graphId="heatmap" :initGraph="initGraph"/>
                    <ProgressIndicator ref="progress" id="heatmap-glasspane" type="spinner"></ProgressIndicator>
                </div>
            </div>
        </div>
        <div class="h-100" id="tasks-tab">
            <div class="h-100 d-flex flex-column align-items-start">
              <div class="align-items-baseline d-flex w-100">
                <h4 class="flex-grow-1" v-if="resource">{{ resource.name }}</h4>
                <button class="btn btn-attention"
                        @click="showHeatmap">
                  <i class="fa fa-chevron-left"></i>&nbsp;Chart
                </button>
              </div>

              <div class="w-100 list-group overflow-auto mt-3">
                    <div class="list-group-item list-group-item-action" v-for="task in tasks">
                        <div class="d-flex align-items-center">
                            <div>
                                <div class="d-flex align-items-center">
                                    <h5 class="m-0">{{ task.name }}</h5>
                                </div>
                                <small class="text-muted">{{ getTimestampString(task.startTs) }} — {{ getTimestampString(task.endTs) }}</small>
                            </div>
                            <div class="flex-fill"></div>
                            <span class="ml-2 badge  badge-secondary">{{ task.projectName }}</span>
                            <span class="ml-2 badge badge-pill badge-danger" v-if="task.maxSumLoad > 100">
                              {{ task.load }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
  import {Vue, Component} from 'vue-facing-decorator';
  import {ResourceAssignmentsList, TeamResourcesList, TeamResourcesLoadsGraph} from '@/Routes';
  import {ProjectDto, ResourceDto, AssignmentDto, TeamDto} from '@/Teams';
  import {handleXhrError} from '@/utils/Ui';
  import {Deferred} from 'jquery';
  import {dateString} from '@/utils/Time';
  import {XmlChartDto, mx} from '@/components/MxGraphChart.vue';
  import MxGraphChart from "@/components/MxGraphChart.vue";
  import ProgressIndicator from '@/components/ProgressIndicator.vue';

  import $ from 'jquery';

  @Component({
    components: {MxGraphChart, ProgressIndicator},
  })
  export default class CapacityHeatmap extends Vue {
    private readonly RESOURCES_DEFAULT = "All resources";
    private readonly PROJECTS_DEFAULT = "All projects";

    private teamRefid: string = "";
    private resources: List<ResourceDto> = [];
    private selectedResources: (ResourceDto | string)[] = [];
    private projects: List<ProjectDto> = [];
    private selectedProjects: (ProjectDto | string)[] = [];
    private delayedUpdate: JQueryDeferred<void>;
    private graphRequestsCounter = 0;
    private tasks: List<AssignmentDto> = [];
    private resource: ResourceDto | null = null;

    public $refs!: {
      heatmap: MxGraphChart,
      progress: ProgressIndicator
    };

    private initGraph(graph: typeof mx.mxGraph) {
      graph.getCursorForCell = function(cell) {
        return CapacityHeatmap.isResourceCell(cell) ? 'pointer' : 'default';
      };
      graph.addListener(mx.mxEvent.CLICK, (sender, evt) => {
        const cell = evt.getProperty("cell");
        if (CapacityHeatmap.isResourceCell(cell)) {
          const email = cell.getAttribute('email', '')
          ResourceAssignmentsList.invoke({
            teamRefid: this.teamRefid,
            email: email
          }).done((tasks: AssignmentDto[]) => {
            this.tasks = tasks.sort((a, b) => a.startTs - b.startTs);
            this.resource = this.resources.find(r => r.email === email)
            showTasks();
          }).fail((jqXhr) => handleXhrError(jqXhr, 'can\'t load resource tasks'));
        }
        evt.consume();
      });
      graph.getLabel = function(cell) {
        if (mx.mxUtils.isNode(cell.value)) {
          return cell.getAttribute('text', '');
        }
      };
      graph.setEnabled(false);
    }

    public updateGraphWithDelay() {
      if (this.selectedProjects.length > 1) {
        this.selectedProjects = this.selectedProjects.filter(p => isProject(p));
      } else if (this.selectedProjects.length == 0) {
        this.selectedProjects = [this.PROJECTS_DEFAULT];
      }
      if (this.selectedResources.length > 1) {
        this.selectedResources = this.selectedResources.filter(r => isResource(r));
      } else if (this.selectedResources.length == 0) {
        this.selectedResources = [this.RESOURCES_DEFAULT];
      }

      if (this.delayedUpdate) {
        this.delayedUpdate.reject();
      }
      this.delayedUpdate = delay(5000);
      this.delayedUpdate.then(() => this.updateGraph());
    }

    public updateGraph(team: TeamDto = null): JQueryPromise<any> {
      this.showHeatmap();
      if (this.$refs.heatmap.isMxGraphSupported()) {
        this.$refs.progress.toggle(true);
        const isTeamChanged = team ? team.refid !== this.teamRefid : false;
        if (isTeamChanged) {
          this.teamRefid = team.refid;
        }
        const useResources = !isTeamChanged && this.selectedResources.reduce((acc, r) => acc && isResource(r), true);
        const useProjects = !isTeamChanged && this.selectedProjects.reduce((acc, p) => acc && isProject(p), true);
        const requestNumber = ++this.graphRequestsCounter;
        return TeamResourcesLoadsGraph.invoke({
          teamRefid: this.teamRefid,
          emails: useResources ? this.selectedResources.map(r => (r as ResourceDto).email) : [],
          projectIds: useProjects ? this.selectedProjects.map(p => (p as ProjectDto).refid) : []
        }).done((graph: XmlChartDto) => {
          // If the last requst takes less time to process than a previous one, it is possible to overwrite the last issued heatmap with a less recent one,
          // thus we apply only the last issued request's result
          if (requestNumber === this.graphRequestsCounter) {
            this.$refs.heatmap.buildChart(graph);
          }
        }).fail((jqXhr) => {
          handleXhrError(jqXhr, 'Capacity heatmap graph loading failed');

        }).always(() => {
          this.$refs.progress.toggle(false);
          if (team) {
            this.updateTeamData(team.projects, isTeamChanged);
          }
        });
      }
    }

    private updateTeamData(projects: List<ProjectDto>, isTeamChanged: Boolean) {
      TeamResourcesList.invoke({ teamRefid: this.teamRefid })
        .done((resources: ResourceDto[]) => {
          this.resources = resources;
          if (!isTeamChanged) {
            // if team hasn't changed, we keep previously selected resources except for ones that got deleted
            const emailsSet = new Set(resources.map(r => r.email));
            this.selectedResources = this.selectedResources.filter(r => !isResource(r) || emailsSet.has(r.email))
          }
          if (isTeamChanged || this.selectedProjects.length === 0) {
            this.selectedResources = [this.RESOURCES_DEFAULT];
          }
        })
        .fail((jqXhr) => handleXhrError(jqXhr, 'can\'t load team resources'));
      this.projects = projects;
      if (!isTeamChanged) {
        // if team hasn't changed, we keep previously selected projects except for ones that got deleted
        const idsSet = new Set(projects.map(p => p.refid));
        this.selectedProjects = this.selectedProjects.filter(p => !isProject(p) || idsSet.has(p.refid))
      }
      if (isTeamChanged || this.selectedProjects.length === 0) {
        this.selectedProjects = [this.PROJECTS_DEFAULT];
      }
    }

    private showHeatmap() {
      $('#tasks-tab').hide();
      $('#heatmap-tab').show();
    }

    private getTimestampString(ts: number): string {
      return dateString(ts);
    }

    private static isResourceCell(cell: typeof mx.mxCell): boolean {
      return cell != null && mx.mxUtils.isNode(cell.value) && cell.value.attributes.getNamedItem("email")
    }
  }

  function showTasks() {
    $('#heatmap-tab').hide();
    $('#tasks-tab').show();
  }

  function delay(ms: number): JQueryDeferred<void> {
    const defer = Deferred<void>();
    setTimeout(function() { defer.resolve(); }, ms || 0);
    return defer;
  }

  function isResource(object: ResourceDto | string): object is ResourceDto {
    return (object as ResourceDto).email !== undefined;
  }

  function isProject(object: ProjectDto | string): object is ProjectDto {
    return (object as ProjectDto).refid !== undefined;
  }
</script>

<style scoped>
    #heatmap-glasspane {
        background-color: whitesmoke;
    }

    #heatmap {
        position: relative;
        flex: 1 1 0;
        min-height: 0;
    }
</style>
